export const GENDER_OPTIONS = [
  "Female",
  "Male",
  "Woman",
  "Man",
  "Non-binary",
  "Prefer not to say",
];

export const RACE_OPTIONS = [
  "Asian",
  "White",
  "Chinese American",
  "Hispanic or Latino",
  "Black or African American",
  "Native American",
  "Pacific Islander",
  "Middle Eastern",
  "South Asian",
  "Southeast Asian",
  "Multiracial",
  "Prefer not to say",
];

export const LANGUAGE_OPTIONS = [
  "English",
  "Spanish",
  "Mandarin",
  "Cantonese",
  "French",
  "Portuguese",
  "Japanese",
  "Korean",
  "German",
  "Italian",
  "Arabic",
];

export const POPULATION_OPTIONS = [
  "Adults (18-64)",
  "Adolescents (13-17)",
  "Children (5-12)",
  "Geriatric (65+)",
  "Couples",
  "LGBTQ+",
  "Veterans",
  "Pregnant/Postpartum",
  "Individuals with Disabilities",
  "Immigrants/Refugees",
  "First Responders",
  "College Students",
];

export const SPECIALTY_OPTIONS = [
  "Anxiety",
  "Depression",
  "PTSD",
  "CBT",
  "Trauma",
  "Mindfulness",
  "Grief",
  "Relationship Issues",
  "ADHD",
  "Anger Management",
  "Life Transitions",
  "Chronic Illness",
  "Self-esteem",
  "Substance Abuse",
  "Eating disorders",
  "OCD",
  "Bipolar Disorder",
  "Sleep Issues",
  "Parenting",
  "Couples Therapy",
  "Family Conflict",
  "Stress Management",
  "Social Anxiety",
  "Autism Spectrum",
  "Phobias",
  "Body Image",
  "Postpartum",
  "Burnout",
  "Addiction",
  "Panic Disorders",
  "Work-Life Balance",
  "Attachment Issues",
  "Narcissistic Abuse",
  "Codependency",
  "Gender Identity",
  "Cultural Adjustment",
  "Elderly Care",
  "Anger Issues",
  "Emotional Regulation",
];

export const MAX_TOP_SPECIALTIES = 3;
export const SPECIALTY_TOP_META = "__TOP__";
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
export const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/gif"];

export const mergeOptions = (options, selected) => {
  const extras = (selected || []).filter(
    (item) => item && !options.includes(item),
  );
  return extras.length ? [...options, ...extras] : options;
};

export const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  return String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

export const parseSpecialties = (value) => {
  const parts = toList(value);
  const meta = parts.find((item) => item.startsWith(`${SPECIALTY_TOP_META}=`));
  const list = parts.filter(
    (item) => !item.startsWith(`${SPECIALTY_TOP_META}=`),
  );

  if (meta) {
    const count = Math.min(
      MAX_TOP_SPECIALTIES,
      Math.max(0, Number(meta.split("=")[1]) || 0),
    );
    return { list, starred: list.slice(0, count) };
  }

  return {
    list,
    starred: list.slice(0, Math.min(MAX_TOP_SPECIALTIES, list.length)),
  };
};

export const serializeSpecialties = (list = [], starred = []) => {
  const selected = (list || []).filter(Boolean);
  const tops = (starred || [])
    .filter((item) => selected.includes(item))
    .slice(0, MAX_TOP_SPECIALTIES);
  const rest = selected.filter((item) => !tops.includes(item));
  const ordered = [...tops, ...rest];
  if (!ordered.length) return "";
  return `${SPECIALTY_TOP_META}=${tops.length},${ordered.join(",")}`;
};

export const getPhoneDigits = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return digits;
};

export const formatPhoneInput = (value) => {
  const digits = getPhoneDigits(value).slice(0, 10);
  const len = digits.length;
  if (!len) return "";
  if (len <= 3) return `(${digits}`;
  if (len <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const getPhoneValidationError = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "Phone is required.";
  const digits = getPhoneDigits(trimmed);
  if (digits.length < 10) return "Enter a 10-digit phone number.";
  if (digits.length > 10) return "Phone number cannot exceed 10 digits.";
  return "";
};

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const buildProfileFormState = (provider) => {
  const { list: specialties, starred: starredSpecialties } = parseSpecialties(
    provider?.specialisation,
  );

  return {
    firstname: provider?.firstname || "",
    lastname: provider?.lastname || "",
    displayName:
      provider?.displayName ||
      `${provider?.firstname || ""} ${provider?.lastname || ""}`.trim(),
    yearsOfExperience:
      provider?.yearsOfExperience !== undefined &&
      provider?.yearsOfExperience !== null
        ? String(provider.yearsOfExperience)
        : "",
    genderIdentity: provider?.genderIdentity || provider?.gender || "",
    raceEthnicity: toList(provider?.raceEthnicity || provider?.race)[0] || "",
    languages: toList(provider?.languages),
    populationsServed: toList(provider?.populationsServed),
    email: provider?.email || "",
    phone: formatPhoneInput(
      provider?.phone || provider?.phoneNumber || provider?.phoneno || "",
    ),
    bio: provider?.bio || provider?.shortBio || "",
    specialties,
    starredSpecialties,
    profilePhotoUrl: provider?.profilePhotoUrl || "",
    videoIntroUrl: provider?.videoIntroUrl || "",
  };
};

export const validateProfileForm = (form) => {
  const errors = {};

  if (!String(form.firstname || "").trim()) {
    errors.firstname = "First name is required.";
  }
  if (!String(form.lastname || "").trim()) {
    errors.lastname = "Last name is required.";
  }
  if (!String(form.displayName || "").trim()) {
    errors.displayName = "Display name is required.";
  }
  if (
    form.yearsOfExperience === "" ||
    form.yearsOfExperience === null ||
    Number(form.yearsOfExperience) < 0 ||
    Number.isNaN(Number(form.yearsOfExperience))
  ) {
    errors.yearsOfExperience = "Years of experience is required.";
  }
  if (!String(form.genderIdentity || "").trim()) {
    errors.genderIdentity = "Gender identity is required.";
  }
  if (!String(form.raceEthnicity || "").trim()) {
    errors.raceEthnicity = "Race & ethnicity is required.";
  }
  if (!form.languages?.length) {
    errors.languages = "Select at least one language.";
  }
  if (!form.populationsServed?.length) {
    errors.populationsServed = "Select at least one population.";
  }
  if (!String(form.email || "").trim()) {
    errors.email = "Email is required.";
  } else if (!EMAIL_REGEX.test(String(form.email).trim())) {
    errors.email = "Enter a valid email address.";
  }
  const phoneError = getPhoneValidationError(form.phone);
  if (phoneError) errors.phone = phoneError;
  if (!String(form.bio || "").trim()) {
    errors.bio = "Short bio is required.";
  }
  if (!form.specialties?.length) {
    errors.specialties = "Select at least one specialty.";
  }
  if (!form.profilePhotoUrl && !form.profilePhotoFile) {
    errors.profilePhoto = "Profile photo is required.";
  }

  return errors;
};

export const isProfileFormComplete = (provider) => {
  const form = buildProfileFormState(provider);
  const errors = validateProfileForm({
    ...form,
    profilePhotoFile: null,
  });
  return Object.keys(errors).length === 0;
};
